import { Checkbox, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import type { DateWindow } from "../../../../utils/milestone-effective-schedule.ts"

const { Text } = Typography

/**
 * How a dependent's new dates get saved:
 * - `follows`: it has an Effort and no dates of its own, so the server dates
 *   it from its dependencies on this save — nothing for the client to write;
 * - `undated`: no dates and no Effort — shown where the dependency puts it,
 *   never saved;
 * - `own`: dates a person chose — saved only when the user asks.
 */
export type ScheduleImpactKind = "follows" | "undated" | "own"

export interface ScheduleImpactEntry {
  id: string
  name: string
  window: DateWindow
  previousWindow: DateWindow | null
  kind: ScheduleImpactKind
}

/** Whether to also save the hand-dated dependents. Read by the
 * confirmation's OK handler, which antd calls outside React. */
export interface ScheduleImpactChoice {
  saveOwnDates: boolean
}

export const defaultScheduleImpactChoice = (): ScheduleImpactChoice => ({
  saveOwnDates: false
})

export const idsToSave = (
  entries: ScheduleImpactEntry[],
  choice: ScheduleImpactChoice
) =>
  choice.saveOwnDates
    ? entries.filter((entry) => entry.kind === "own").map((entry) => entry.id)
    : []

const NOTE_KEY: Record<ScheduleImpactKind, string | null> = {
  follows: "milestoneScheduleImpact.followsItsDependency",
  undated: "milestoneScheduleImpact.noDatesOfItsOwn",
  own: null
}

export function MilestoneScheduleImpactContent({
  lede,
  entries,
  choice
}: {
  lede: string
  entries: ScheduleImpactEntry[]
  /** Mutated in place, so the caller's OK handler reads the latest ticks. */
  choice: ScheduleImpactChoice
}) {
  const { t } = useTranslation()
  const [saveOwnDates, setSaveOwnDates] = useState(choice.saveOwnDates)

  const ownDatesCount = entries.filter((e) => e.kind === "own").length

  return (
    <>
      <p>{lede}</p>
      <ul className="list-disc pl-4">
        {entries.map(({ id, name, window, previousWindow, kind }) => (
          <li key={id}>
            {name}:{" "}
            {previousWindow && (
              <>
                {previousWindow.start.format("MMM D")} –{" "}
                {previousWindow.end.format("MMM D")} →{" "}
              </>
            )}
            {window.start.format("MMM D")} – {window.end.format("MMM D")}
            {NOTE_KEY[kind] ? (
              <Text type="secondary"> {t(NOTE_KEY[kind] as string)}</Text>
            ) : null}
          </li>
        ))}
      </ul>
      {ownDatesCount ? (
        <>
          <p>{t("milestoneScheduleImpact.shownShifted")}</p>
          <Checkbox
            checked={saveOwnDates}
            onChange={(e) => {
              choice.saveOwnDates = e.target.checked
              setSaveOwnDates(e.target.checked)
            }}
          >
            {t("milestoneScheduleImpact.saveOwnDates", {
              count: ownDatesCount
            })}
          </Checkbox>
        </>
      ) : null}
    </>
  )
}
